/**
 * Normalizes legacy / auto-generated review-summary HTML so that each visible
 * line becomes its own block before it is handed to Lexical's
 * `$generateNodesFromDOM`.
 *
 * Why: content authored in the old react-rte editor (and most auto-generated
 * summaries) separates lines with `<br>` soft line breaks inside a single
 * `<p>`/`<div>`, or with no wrapper at all. Lexical imports each such case as a
 * SINGLE paragraph block holding every line. Because indentation in Lexical is
 * a per-block property, indenting/outdenting one line then rewrites the entire
 * block — which flattens the whole summary and replaces its DOM subtree,
 * snapping the scroll position. Splitting `<br>`-separated lines into separate
 * `<p>` blocks makes each line independently editable and indentable.
 *
 * The transform is idempotent: `<p>` -per-line content is left untouched, so
 * once a summary is edited and re-saved it stays "healed".
 *
 * Pure DOM manipulation (no Lexical dependency) so it is unit-testable under
 * jsdom without the Lexical mock used by this package's test suite.
 */

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

// Block-level elements that already represent their own structure and must not
// be merged into paragraphs or split. Lists are intentionally left alone.
const PASSTHROUGH_TAGS = new Set(["UL", "OL", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE", "TABLE", "PRE"]);

const isBr = (node: Node): boolean => node.nodeType === ELEMENT_NODE && (node as Element).tagName === "BR";

const isWhitespaceText = (node: Node): boolean => node.nodeType === TEXT_NODE && !(node.textContent ?? "").trim();

const isPassthroughBlock = (node: Node): boolean =>
  node.nodeType === ELEMENT_NODE && PASSTHROUGH_TAGS.has((node as Element).tagName);

const isSplittableBlock = (node: Node): boolean =>
  node.nodeType === ELEMENT_NODE && ((node as Element).tagName === "P" || (node as Element).tagName === "DIV");

/** True if the segment carries any real content worth keeping as a paragraph. */
const segmentHasContent = (nodes: Node[]): boolean =>
  nodes.some((n) => n.nodeType === ELEMENT_NODE || (n.nodeType === TEXT_NODE && (n.textContent ?? "").trim()));

/**
 * Splits a `<p>`/`<div>`'s children at each direct `<br>` child into one
 * paragraph per line, preserving inline formatting (`<strong>`, `<em>`, `<a>`,
 * text) within each line. Returns the resulting `<p>` elements.
 */
function splitElementOnBr(el: Element, doc: Document): HTMLParagraphElement[] {
  const paragraphs: HTMLParagraphElement[] = [];
  let segment: Node[] = [];

  const flush = () => {
    if (segmentHasContent(segment)) {
      const p = doc.createElement("p");
      segment.forEach((n) => p.appendChild(n));
      paragraphs.push(p);
    }
    segment = [];
  };

  // Snapshot children: appendChild below detaches them from `el` as we go.
  Array.from(el.childNodes).forEach((child) => {
    if (isBr(child)) {
      flush();
    } else {
      segment.push(child);
    }
  });
  flush();

  // Never drop a block entirely (e.g. an element that held only `<br>`s).
  if (paragraphs.length === 0) {
    paragraphs.push(doc.createElement("p"));
  }
  return paragraphs;
}

/**
 * Rewrites `doc.body` in place so that `<br>`-separated and loose top-level
 * inline content become discrete `<p>` blocks. Block elements in
 * {@link PASSTHROUGH_TAGS} and `<br>`-free `<p>`/`<div>`s are left as-is.
 */
export function splitLineBreaksIntoParagraphs(doc: Document): void {
  const body = doc.body;
  if (!body) {
    return;
  }

  const result: Node[] = [];
  let inlineRun: Node[] = [];

  const flushInlineRun = () => {
    if (segmentHasContent(inlineRun)) {
      const p = doc.createElement("p");
      inlineRun.forEach((n) => p.appendChild(n));
      result.push(p);
    }
    inlineRun = [];
  };

  // Snapshot first; we move nodes into new paragraphs as we iterate.
  Array.from(body.childNodes).forEach((node) => {
    if (isBr(node)) {
      // Loose top-level line break: ends the current line.
      flushInlineRun();
    } else if (isPassthroughBlock(node)) {
      flushInlineRun();
      result.push(node);
    } else if (isSplittableBlock(node)) {
      flushInlineRun();
      const el = node as Element;
      if (Array.from(el.childNodes).some(isBr)) {
        splitElementOnBr(el, doc).forEach((p) => result.push(p));
      } else {
        result.push(node);
      }
    } else if (isWhitespaceText(node)) {
      // Ignore inter-block whitespace text nodes.
    } else {
      // Loose inline content (text / <span> / <strong> / <a> ...): buffer it
      // into the current line.
      inlineRun.push(node);
    }
  });
  flushInlineRun();

  while (body.firstChild) {
    body.removeChild(body.firstChild);
  }
  result.forEach((n) => body.appendChild(n));
}

/** Convenience wrapper that normalizes an HTML string and returns the result. */
export function normalizeLegacyHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  splitLineBreaksIntoParagraphs(doc);
  return doc.body.innerHTML;
}
